import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle2, ArrowRight, Lock, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();

  async function onSubmit(e) {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (password !== confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      setUser(data.user);
      setDone(true);
      toast.success("Password aggiornata! Sei stato loggato automaticamente.");
      setTimeout(() => nav("/dashboard", { replace: true }), 1200);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-5">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-black">Token mancante</h1>
          <p className="mt-3 text-sm text-slate-400">Il link non è valido. Richiedi un nuovo reset dalla pagina di login.</p>
          <Link to="/password-dimenticata" className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
            Richiedi nuovo link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-5">
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute top-0 -left-32 w-[500px] h-[500px] bg-emerald-500/15 rounded-full blur-3xl"/>
      </div>
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8">
          <ArrowLeft size={14}/> Torna al login
        </Link>
        <div className="text-[11px] tracking-[0.4em] text-emerald-400 uppercase font-bold mb-2">
          <Lock size={12} className="inline mr-1"/> Nuova password
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight" data-testid="reset-title">Imposta una nuova password</h1>

        {done ? (
          <div className="mt-8 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center" data-testid="reset-done-card">
            <CheckCircle2 size={40} className="text-emerald-400 mx-auto mb-3"/>
            <h2 className="font-bold text-lg text-white">Password aggiornata!</h2>
            <p className="mt-2 text-sm text-slate-300">Reindirizzamento alla dashboard…</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs tracking-widest text-slate-400 uppercase font-bold block mb-2">🔒 Nuova password</label>
              <input data-testid="reset-password"
                className="w-full bg-slate-900 border border-slate-700 rounded-md px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-colors"
                type="password" required minLength={6}
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="min 6 caratteri"/>
            </div>
            <div>
              <label className="text-xs tracking-widest text-slate-400 uppercase font-bold block mb-2">🔒 Conferma password</label>
              <input data-testid="reset-password-confirm"
                className="w-full bg-slate-900 border border-slate-700 rounded-md px-4 py-3 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-colors"
                type="password" required minLength={6}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="ripeti la nuova password"/>
            </div>
            <button type="submit" disabled={loading}
              data-testid="reset-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold uppercase tracking-wider disabled:opacity-50 shadow-lg shadow-emerald-500/20 transition-all">
              {loading ? "Aggiornamento…" : "Imposta e accedi"} <ArrowRight size={16}/>
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
